/**
 * CPFRequiredModal.js - Modal de Atualização Obrigatória de CPF
 * 
 * Este modal aparece para usuários que não informaram CPF no cadastro.
 * O acesso à plataforma fica bloqueado até que o CPF seja informado.
 */

import React, { useState } from 'react';
import { Shield, AlertTriangle, CheckCircle, CreditCard, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL, API_URL } from '../config/backendUrl';

const CPFRequiredModal = ({ isOpen, onSuccess, userName, userEmail }) => {
  const [cpf, setCpf] = useState('');
  const [password, setPassword] = useState(''); // Senha para confirmação
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Formatar CPF automaticamente
  const handleCPFChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    
    // Formata como CPF (XXX.XXX.XXX-XX)
    if (value.length <= 11) {
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    // Formata como CNPJ (XX.XXX.XXX/XXXX-XX)
    else {
      value = value.replace(/^(\d{2})(\d)/, '$1.$2');
      value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
      value = value.replace(/\.(\d{3})(\d)/, '.$1/$2');
      value = value.replace(/(\d{4})(\d)/, '$1-$2');
    }
    
    setCpf(value);
    setError('');
  };

  // Validar CPF
  const validateCPF = (cpfStr) => {
    const cpfClean = cpfStr.replace(/\D/g, '');
    if (cpfClean.length !== 11) return false;
    if (/^(\d)\1+$/.test(cpfClean)) return false;
    
    let soma = 0;
    for (let i = 0; i < 9; i++) soma += parseInt(cpfClean[i]) * (10 - i);
    let resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpfClean[9])) return false;
    
    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(cpfClean[i]) * (11 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpfClean[10])) return false;
    
    return true;
  };

  // Validar CNPJ
  const validateCNPJ = (cnpjStr) => {
    const cnpjClean = cnpjStr.replace(/\D/g, '');
    if (cnpjClean.length !== 14) return false;
    if (/^(\d)\1+$/.test(cnpjClean)) return false;
    
    let tamanho = cnpjClean.length - 2;
    let numeros = cnpjClean.substring(0, tamanho);
    const digitos = cnpjClean.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;
    
    for (let i = tamanho; i >= 1; i--) {
      soma += numeros.charAt(tamanho - i) * pos--;
      if (pos < 2) pos = 9;
    }
    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) return false;
    
    tamanho = tamanho + 1;
    numeros = cnpjClean.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += numeros.charAt(tamanho - i) * pos--;
      if (pos < 2) pos = 9;
    }
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(1))) return false;
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const cpfClean = cpf.replace(/\D/g, '');
    
    // Validar senha
    if (!password || password.length < 6) {
      setError('Por favor, confirme sua senha para atualizar o CPF');
      return;
    }
    
    // Validar CPF/CNPJ
    if (!cpfClean) {
      setError('Por favor, informe seu CPF ou CNPJ');
      return;
    }
    
    if (cpfClean.length === 11) {
      if (!validateCPF(cpfClean)) {
        setError('CPF inválido. Por favor, verifique o número informado.');
        return;
      }
    } else if (cpfClean.length === 14) {
      if (!validateCNPJ(cpfClean)) {
        setError('CNPJ inválido. Por favor, verifique o número informado.');
        return;
      }
    } else {
      setError('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/auth/update-cpf-required`,
        { 
          cpf_cnpj: cpf,
          password: password  // Enviar senha para confirmação
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('CPF atualizado com sucesso! Seu acesso foi liberado.');
        
        if (response.data.certificates_updated > 0) {
          toast.success(`${response.data.certificates_updated} certificados atualizados com seu CPF!`);
        }
        
        onSuccess();
      }
    } catch (err) {
      console.error('Erro ao atualizar CPF:', err);
      // Mostrar mensagem de erro mais específica
      let errorMessage = 'Erro ao atualizar CPF. Tente novamente.';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.status === 400) {
        errorMessage = 'CPF/CNPJ inválido ou já cadastrado por outro usuário.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Erro no servidor. Por favor, tente novamente em alguns instantes.';
      } else if (!err.response) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-3 rounded-full">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Atualização Obrigatória</h2>
              <p className="text-amber-100">Cadastro de CPF necessário</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Saudação */}
          {userName && (
            <p className="text-gray-600 mb-4">
              Olá, <strong>{userName}</strong>!
            </p>
          )}

          {/* Aviso Principal */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6">
            <p className="text-amber-800 text-sm leading-relaxed">
              Para garantir a <strong>validade jurídica dos certificados</strong> de registro das suas obras, 
              é obrigatório informar seu <strong>CPF ou CNPJ</strong>.
            </p>
          </div>

          {/* Informações Importantes */}
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-gray-600 text-sm">
                Os certificados emitidos pela plataforma são <strong>vinculados diretamente ao CPF do autor</strong>, 
                conforme a legislação de direitos autorais.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-gray-600 text-sm">
                <strong>Enquanto o CPF não for informado</strong>, o acesso à plataforma ficará temporariamente bloqueado.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-gray-600 text-sm">
                Após o preenchimento, seu <strong>acesso será liberado automaticamente</strong> e seus 
                certificados serão atualizados com seu CPF.
              </p>
            </div>
          </div>

          {/* Formulário */}
          <form onSubmit={handleSubmit}>
            {/* Campo CPF/CNPJ */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CPF ou CNPJ <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={cpf}
                  onChange={handleCPFChange}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all text-lg"
                  placeholder="000.000.000-00"
                  maxLength={18}
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Digite apenas números - a formatação é automática
              </p>
            </div>

            {/* Campo Senha para Confirmação */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirme sua Senha <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                  placeholder="Digite sua senha atual"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Para sua segurança, confirme sua senha para atualizar o CPF
              </p>
            </div>

            {/* Erro */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {/* Botão */}
            <button
              type="submit"
              disabled={loading || !cpf || !password}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white py-4 rounded-xl font-bold text-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                  Atualizando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Confirmar CPF e Liberar Acesso
                </>
              )}
            </button>
          </form>

          {/* Rodapé */}
          <p className="text-xs text-gray-400 text-center mt-4">
            Seus dados são protegidos conforme nossa política de privacidade.
            <br />
            O CPF é usado exclusivamente para fins de registro autoral.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CPFRequiredModal;
